import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { memo, type ReactElement, useCallback, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { Box, HStack, ScrollEdgeFade, Text, useScrollEdges } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { BoardData, Lead } from '../../../types/leads';
import { useDragEdgeGlide } from '../hooks/useDragEdgeGlide';
import { DragProvider } from './DragContext';
import { KanbanColumn } from './KanbanColumn';

const COLUMN_GAP = 12;
const MOBILE_BOARD_INSET = 32;
const TABLET_COLUMN_WIDTH = 260;
const DESKTOP_COLUMN_WIDTH = 300;
const SCROLL_BUTTON_SIZE = 40;
const SCROLL_BUTTON_ICON_SIZE = 20;
const SCROLL_BUTTON_ICON_STROKE = 2.25;
const SCROLL_BUTTON_ACTIVE_SCALE = 1.06;
const SCROLL_BUTTON_TRANSITION = 'border-color 0.15s ease, transform 0.15s ease';

type ScrollDirection = 'previous' | 'next';

interface KanbanBoardProps {
  board: BoardData | undefined;
  isLoading: boolean;
  error: Error | null;
  onLeadPress?: (lead: Lead) => void;
  onDrop?: (leadId: string, fromStageId: string, toStageId: string) => void;
}

export function KanbanBoard({
  board,
  isLoading,
  error,
  onLeadPress,
  onDrop,
}: Readonly<KanbanBoardProps>): ReactElement {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobile = width < tokens.breakpoints.tablet;
  const isTablet = width >= tokens.breakpoints.tablet && width < tokens.breakpoints.desktop;
  const dragEnabled = !isMobile;

  // The board is almost always wider than the viewport, so the stage at the
  // right edge gets sliced. On its own that slice reads as broken content
  // rather than as "there is more this way", and the platform scrollbar is a
  // transient overlay that says nothing until you already scroll. These edge
  // fades are the standing affordance. Disabled on mobile, where the board
  // stacks vertically instead of scrolling sideways.
  const { atStart, atEnd, offset, maxOffset, scrollProps } = useScrollEdges(true, !isMobile);
  const scrollRef = useRef<ScrollView>(null);
  const hasStages = !!board && board.stages.length > 0;

  const resolveColumnWidth = (): number => {
    if (isMobile) return width - MOBILE_BOARD_INSET;
    if (isTablet) return TABLET_COLUMN_WIDTH;
    return DESKTOP_COLUMN_WIDTH;
  };
  const columnWidth = resolveColumnWidth();
  const columnStep = columnWidth + COLUMN_GAP;

  const scrollByColumn = useCallback(
    (direction: ScrollDirection): void => {
      const delta = direction === 'next' ? columnStep : -columnStep;
      const target = Math.min(maxOffset, Math.max(0, offset + delta));
      scrollRef.current?.scrollTo({ x: target, animated: true });
    },
    [columnStep, maxOffset, offset],
  );

  useDragEdgeGlide(scrollRef, dragEnabled && !isLoading && hasStages);

  if (isLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
        <ActivityIndicator size="large" color={colors.primary[500]} />
        <Text variant="body" color={colors.text.tertiary} style={{ marginTop: 12 }}>
          {t('leads.pipeline.loading')}
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
        <Text variant="body" color={colors.status.error.main}>
          {t('leads.pipeline.failed')}
        </Text>
        <Text variant="caption" color={colors.text.tertiary} style={{ marginTop: 4 }}>
          {error.message}
        </Text>
      </Box>
    );
  }

  if (!board || board.stages.length === 0) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
        <Text variant="h6" color={colors.text.secondary}>
          {t('leads.pipeline.noStages')}
        </Text>
        <Text variant="caption" color={colors.text.tertiary} style={{ marginTop: 4 }}>
          {t('leads.pipeline.noStagesHint')}
        </Text>
      </Box>
    );
  }

  const totalLeads = board.stages.reduce((sum, s) => sum + s.count, 0);

  // Same Yoga collapse precedent as funnel.tsx / Leads commit 12534c2:
  // VStack `spacing` (→ gap) + intrinsic sibling + flex:1 child = flex child
  // collapses to 0 height on iOS. Plain View + explicit marginBottom keeps
  // the vertical rhythm without tripping the bug.
  return (
    <DragProvider onDrop={dragEnabled ? onDrop : undefined}>
      <View style={{ flex: 1 }}>
        <HStack
          spacing="sm"
          align="center"
          style={{
            flexWrap: 'wrap',
            marginBottom: tokens.spacing.md,
            // Mobile: align this row with the card titles below
            // (sm + md = 20 from the padded content edge).
            ...(isMobile ? { paddingHorizontal: tokens.spacing.sm + tokens.spacing.md } : {}),
          }}
        >
          <Text variant="caption" color={colors.text.tertiary}>
            {totalLeads} lead{totalLeads === 1 ? '' : 's'} across {board.stages.length} stages
          </Text>
          {dragEnabled && (
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {t('leads.pipeline.dragHint')}
            </Text>
          )}
        </HStack>

        <View style={{ flex: 1 }}>
          <ScrollView
            ref={scrollRef}
            testID="kanban_board_container"
            horizontal={!isMobile}
            accessibilityRole="list"
            accessibilityLabel={t('leads.pipeline.stagesLabel')}
            {...(isMobile ? { showsVerticalScrollIndicator: false } : {})}
            showsHorizontalScrollIndicator={!isMobile && Platform.OS === 'web'}
            {...scrollProps}
            // A trailing gutter so the final stage ends with the same breathing
            // room as the first instead of butting against the container edge.
            contentContainerStyle={{
              gap: COLUMN_GAP,
              paddingBottom: 16,
              ...(isMobile ? {} : { paddingRight: tokens.spacing.lg }),
            }}
            style={[{ flex: 1 }, !isMobile && boardScrollbarStyle]}
          >
            {board.stages.map((stage) => (
              <KanbanColumn
                key={stage.id}
                stage={stage}
                columnWidth={columnWidth}
                dragEnabled={dragEnabled}
                onLeadPress={onLeadPress}
              />
            ))}
          </ScrollView>

          {/* Inset clears the horizontal scrollbar so the fade never sits on
              top of the control it is pointing at. */}
          {atStart && (
            <ScrollEdgeFade edge="left" color={colors.background.primary} inset={16} size={40} />
          )}
          {atEnd && (
            <ScrollEdgeFade edge="right" color={colors.background.primary} inset={16} size={40} />
          )}
          {atStart && (
            <BoardScrollButton
              direction="previous"
              label={t('leads.kanban.scrollPrevious')}
              onScroll={scrollByColumn}
            />
          )}
          {atEnd && (
            <BoardScrollButton
              direction="next"
              label={t('leads.kanban.scrollNext')}
              onScroll={scrollByColumn}
            />
          )}
        </View>
      </View>
    </DragProvider>
  );
}

const boardScrollbarStyle = Platform.select({
  web: {
    scrollbarWidth: 'thin',
    scrollbarColor: `${colors.border.strong} transparent`,
  } as unknown as ViewStyle,
  default: {},
});

interface BoardScrollButtonProps {
  direction: ScrollDirection;
  label: string;
  onScroll: (direction: ScrollDirection) => void;
}

const BoardScrollButton = memo(function BoardScrollButton({
  direction,
  label,
  onScroll,
}: Readonly<BoardScrollButtonProps>): ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = direction === 'previous' ? ChevronLeft : ChevronRight;
  const handlePress = useCallback((): void => onScroll(direction), [onScroll, direction]);

  return (
    <Pressable
      testID={`kanban_scroll_${direction}`}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={({ pressed }) => [
        styles.scrollButton,
        direction === 'previous' ? styles.scrollButtonPrevious : styles.scrollButtonNext,
        (isHovered || pressed) && styles.scrollButtonActive,
      ]}
    >
      <Icon
        size={SCROLL_BUTTON_ICON_SIZE}
        color={colors.text.primary}
        strokeWidth={SCROLL_BUTTON_ICON_STROKE}
      />
    </Pressable>
  );
});

const styles = StyleSheet.create({
  scrollButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -SCROLL_BUTTON_SIZE / 2,
    width: SCROLL_BUTTON_SIZE,
    height: SCROLL_BUTTON_SIZE,
    borderRadius: SCROLL_BUTTON_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.elevated,
    borderWidth: 1,
    borderColor: colors.border.default,
    ...tokens.shadow?.sm,
    ...Platform.select({
      web: { cursor: 'pointer', transition: SCROLL_BUTTON_TRANSITION },
      default: {},
    }),
  } as ViewStyle,
  scrollButtonPrevious: {
    left: tokens.spacing.xs,
  },
  scrollButtonNext: {
    right: tokens.spacing.xs,
  },
  scrollButtonActive: {
    borderColor: colors.primary[500],
    transform: [{ scale: SCROLL_BUTTON_ACTIVE_SCALE }],
  },
});
